import React, { createContext, useState, useMemo, useContext, useEffect, useLayoutEffect } from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { apiFetch } from '../utils/apiConfig';
import {
  applySkin,
  readStoredMode,
  readStoredSkinId,
  THEME_MODE_KEY,
  THEME_SKIN_KEY,
} from './applySkin';
import { getSkin, listSkins } from './skins';

const CustomThemeContext = createContext({
  toggleTheme: () => {},
  setInitialTheme: () => {},
  setSkin: () => {},
  setTheme: () => {},
  mode: 'light',
  skinId: 'default',
  skins: [],
});

export const useThemeContext = () => useContext(CustomThemeContext);

export const CustomThemeProvider = ({ children }) => {
  const [mode, setMode] = useState(readStoredMode);
  const [skinId, setSkinId] = useState(readStoredSkinId);
  const [userDataLoaded, setUserDataLoaded] = useState(false);

  const saveDarkModePreference = async (isDarkMode) => {
    try {
      const response = await apiFetch('/auth/me/darkmode', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ uses_darkmode: isDarkMode }),
      });

      if (response.ok) {
        console.log(`Dark mode preference saved: ${isDarkMode ? 'enabled' : 'disabled'}`);
      } else {
        console.warn('Failed to save dark mode preference:', response.status);
      }
    } catch (error) {
      console.warn('Error saving dark mode preference:', error);
    }
  };

  const persistMode = (nextMode) => {
    try {
      localStorage.setItem(THEME_MODE_KEY, nextMode);
    } catch {
      // ignore
    }
  };

  const persistSkin = (nextSkinId) => {
    try {
      localStorage.setItem(THEME_SKIN_KEY, nextSkinId);
    } catch {
      // ignore
    }
  };

  const colorMode = useMemo(
    () => ({
      toggleTheme: () => {
        setMode((prevMode) => {
          const newMode = prevMode === 'light' ? 'dark' : 'light';
          if (userDataLoaded) {
            saveDarkModePreference(newMode === 'dark');
          }
          persistMode(newMode);
          return newMode;
        });
      },
      setInitialTheme: (initialMode) => {
        const next = initialMode === 'dark' ? 'dark' : 'light';
        setMode(next);
        setUserDataLoaded(true);
        persistMode(next);
      },
      setSkin: (nextSkinId) => {
        const resolved = getSkin(nextSkinId).id;
        setSkinId(resolved);
        persistSkin(resolved);
      },
      setTheme: (nextSkinId, nextMode) => {
        const resolvedSkin = getSkin(nextSkinId).id;
        const resolvedMode = nextMode === 'dark' ? 'dark' : 'light';
        setSkinId(resolvedSkin);
        persistSkin(resolvedSkin);
        if (resolvedMode !== mode) {
          if (userDataLoaded) {
            saveDarkModePreference(resolvedMode === 'dark');
          }
          setMode(resolvedMode);
          persistMode(resolvedMode);
        }
      },
      mode,
      skinId,
      skins: listSkins(),
    }),
    [mode, skinId, userDataLoaded]
  );

  const theme = useMemo(() => applySkin(skinId, mode), [skinId, mode]);

  useLayoutEffect(() => {
    applySkin(skinId, mode);
    persistSkin(skinId);
    persistMode(mode);
  }, [skinId, mode]);

  useEffect(() => {
    const onStorage = (event) => {
      if (event.key === THEME_MODE_KEY && event.newValue) {
        setMode(event.newValue === 'dark' ? 'dark' : 'light');
      }
      if (event.key === THEME_SKIN_KEY && event.newValue) {
        setSkinId(getSkin(event.newValue).id);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  return (
    <CustomThemeContext.Provider value={colorMode}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </CustomThemeContext.Provider>
  );
};
